import { beebeeAjaxPostJson } from 'beebee-base/lib/utility/ajax';
import { normalizeCurrency, clearAttribute, currency2Number } from 'beebee-base/lib/utility';
import { set } from '@ember/object';
import getConfig from 'beebee-base/lib/get-config';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — shared Ember Data helper functions consumed by this
 *              addon's model files (src/models/*.js); NOT a Glimmer component
 *              and NOT itself the model registry (the models live in src/models/).
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      beebee-base/lib/utility/ajax (beebeeAjaxPostJson),
 *              beebee-base/lib/utility (clearAttribute, currency2Number,
 *              normalizeCurrency), beebee-base/lib/get-config, @ember/object (set)
 *   siblings:  -
 *   tests:     tests/unit/lib/utility-test.js
 *   gotchas:   in _setAttributes, currency2Number must run ONLY on the
 *              currency-typed custom field's numeric attribute — applying it to
 *              every eachAttribute() entry (including `value`, an @attr('string'))
 *              silently replaced stored text with 0 on the next save.
 *
 * Shared helpers imported by several beebee-base-model model files: custom-field
 * label formatting (_createLabel), the generic saveAll payload builder used by
 * contact/project/event/etc. models (_saveAll), custom-field/address write
 * batching (_setAttributes), sorted custom-field lists, and a safe reload guard.
 */
function _createLabel(model, cSubLabel, session, intl, currency) {
  const timeZone = session?.me?.time_zone || 'Europe/Rome';
  const defaultCurrency = 'EUR';
  const formattedCurrency = normalizeCurrency(currency || session?.option_field?.intl?.currency || defaultCurrency);
  const formattedValues = cSubLabel.map(custom => {
    const fieldValue = model[custom.field];
    if (custom.type === 'date') {
      const dateObject = fieldValue ? new Date(fieldValue) : null;
      return dateObject && !isNaN(dateObject.getTime()) ? intl.formatDate(dateObject, {
        timeZone
      }) : null;
    } else if (custom.type === 'currency') {
      return typeof fieldValue === 'number' && !isNaN(fieldValue) ? intl.formatNumber(fieldValue, {
        style: 'currency',
        currency: formattedCurrency
      }) : null;
    } else {
      return fieldValue;
    }
  });
  return formattedValues.filter(v => v != null).join(' ');
}
async function _setAttributes(model, field, array) {
  if (!model || typeof model.get !== 'function') {
    console.warn('_setAttributes: model non valido');
    return;
  }
  if (!field || typeof field !== 'string') {
    console.warn('_setAttributes: field non valido');
    return;
  }
  if (!Array.isArray(array)) {
    console.warn('_setAttributes: array non è un array valido');
    return;
  }
  let fieldArray;
  try {
    fieldArray = await model.get(field);
  } catch (err) {
    console.error(`_setAttributes: errore nel recupero di ${field}`, err);
    return;
  }
  if (!fieldArray || typeof fieldArray.forEach !== 'function') {
    console.warn(`_setAttributes: ${field} non è un array o non è iterabile`);
    return;
  }
  fieldArray.forEach(item => {
    if (!item || typeof item.getProperties !== 'function') {
      console.warn('_setAttributes: elemento non valido', item);
      return;
    }
    const props = item.getProperties('id', 'organization_custom_field');
    const id = props.id ?? null;
    const organization_custom_field_id = props.organization_custom_field?.id ?? null;
    let attributes = null;
    if (item.hasDirtyAttributes) {
      attributes = {};
      if (typeof item.eachAttribute === 'function') {
        const isCurrency = item.get('organization_custom_field.data_type') === 'currency';
        item.eachAttribute(name => {
          const value = clearAttribute(item.get(name));

          // The currency conversion belongs to the NUMERIC attribute only. It used
          // to key off the field's data_type alone and then run EVERY attribute
          // through currency2Number — including `value`, which is @attr('string').
          // For a currency-typed custom field that meant
          // currency2Number('<p>text</p>') → parseFloat(...) || 0 → 0, so stored
          // text was silently replaced by 0 on the next save. (booleans came back
          // untouched, so `value` was the only casualty.)
          attributes[name] = isCurrency && name === 'number_value' ? currency2Number(value) : value;
        });
      }
    }
    array.push({
      id,
      organization_custom_field_id,
      attributes
    });
  });
}
function _safeReload(model) {
  if (model.isSaving || model.isLoading || model.isDeleted) {
    return model;
  }
  return model.reload();
}
function _sortedCustomFields(custom_fields, isDeleted) {
  if (!isDeleted && Array.isArray(custom_fields)) {
    return custom_fields.slice().sort((a, b) => {
      const ra = parseInt(a.organization_custom_field?.rank, 10);
      const rb = parseInt(b.organization_custom_field?.rank, 10);
      return (Number.isNaN(ra) ? Number.MAX_SAFE_INTEGER : ra) - (Number.isNaN(rb) ? Number.MAX_SAFE_INTEGER : rb);
    });
  } else {
    return [];
  }
}
async function _saveAll(model, session, saveAllRoute) {
  const mapById = async relation => {
    const rel = await model[relation];
    return rel?.map(item => item.id) || [];
  };
  const data = {
    id: model.id,
    attributes: {},
    relationships: {
      contact_state: model.contact_state?.id,
      product_state: model.product_state?.id,
      project_state: model.project_state?.id,
      event_state: model.event_state?.id,
      workhour_state: model.workhour_state?.id,
      custom_fields: [],
      contacts: await mapById('contacts'),
      related_contacts: await mapById('related_contacts'),
      products: await mapById('products'),
      related_products: await mapById('related_products'),
      projects: await mapById('projects'),
      related_projects: await mapById('related_projects'),
      events: await mapById('events'),
      related_events: await mapById('related_events'),
      payments: await mapById('payments'),
      workhours: await mapById('workhours'),
      related_workhours: await mapById('related_workhours'),
      addresses: [],
      expenses: await mapById('expenses'),
      invoices: await mapById('invoices'),
      reminders: await mapById('reminders'),
      todolists: await mapById('todolists'),
      contact_consensis: [],
      project_consensis: []
    }
  };
  model.eachAttribute(function (name, meta) {
    let trimVal = clearAttribute(model.get(name));
    if (meta.options?.currency) {
      trimVal = currency2Number(model.get(name));
    }
    data.attributes[name] = trimVal;
    set(model, name, trimVal);
  });
  for (const relation of ['custom_fields', 'addresses']) {
    await _setAttributes(model, relation, data.relationships[relation]);
  }
  for (const relation of ['contact_consensis', 'project_consensis']) {
    if (Array.isArray(data.relationships[relation])) {
      await _setAttributes(model, relation, data.relationships[relation]);
    }
  }
  return beebeeAjaxPostJson(`${getConfig(model).APP.urlApi}/${saveAllRoute}`, session.token, data);
}
function applyStyle(text = '', styleName = null) {
  // Ember Data string attrs are null when absent; String(null) would yield
  // the literal "null" shown in UI labels, so treat null/undefined as ''.
  if (text === null || text === undefined) {
    return '';
  }
  if (typeof text !== 'string') {
    text = String(text);
  }
  if (styleName === 'upperCase') {
    return text.toUpperCase();
  } else if (styleName === 'capitalize') {
    return text.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  } else if (styleName === 'lowerCase') {
    return text.toLowerCase();
  }
  return text;
}

export { _createLabel, _safeReload, _saveAll, _sortedCustomFields, applyStyle };
//# sourceMappingURL=utility.js.map
